/* =====================================================================
   PERSISTENZ (Stufe 1 + Stufe 2, seit Stand 34 JE EVENT) — Naht zum
   eigenen Server (Node.js + MySQL, microsoccer.social / cPanel).

   Prinzip unverändert: EINE Wahrheit bleibt der Reducer-State in
   microsoccer-rahmen.jsx. Dieses Modul ist reiner Transport — es kennt
   keine Turnier-Logik, nur "State laden/speichern", "gibt es inzwischen
   einen neueren Stand?" und (Stand 34) die Event-Liste.

   Stand 34: jede Speicherung gehört zu GENAU EINEM Event (eventId). Ein
   Event kann nie ein anderes überschreiben (Konzept §10).

   Konfiguration (optional, sonst sinnvolle Defaults):
     window.MICROSOCCER_API_BASE  — z. B. "https://matchday.microsoccer.social/api"
                                      (Default: "/api")
     window.MICROSOCCER_API_TOKEN — gemeinsames Server-Token (X-Api-Token-Header)
   ===================================================================== */

const API_BASE =
  (typeof window !== "undefined" && window.MICROSOCCER_API_BASE) || "/api";
const API_TOKEN =
  (typeof window !== "undefined" && window.MICROSOCCER_API_TOKEN) || "";

function buildHeaders(extra) {
  const h = { "Content-Type": "application/json", ...(extra || {}) };
  if (API_TOKEN) h["X-Api-Token"] = API_TOKEN;
  return h;
}

/* ---------- Stand 39: Sitzung je Event + Server-Uhr ----------
   Die Sitzung (Academy-Login, Gast-Code, Headcoach-Code) liegt nur auf diesem Gerät (localStorage) und geht als
   Header X-Matchday-Sitzung mit. Ohne Sitzung liefert der Server bis zur Siegerehrung keine Tore/Codes.
   serverJetzt(): Uhrzeit des Servers (alle Handys rechnen Anpfiff, Restzeit und Endsignal mit derselben Uhr). */
const SITZ_KEY = (eventId) => `microsoccer_sitzung:${eventId}`;
export function holeSitzung(eventId) { try { const x = JSON.parse(window.localStorage.getItem(SITZ_KEY(eventId)) || "null"); return x && x.token ? x : null; } catch (_) { return null; } }
export function setzeSitzung(eventId, s) { try { if (s && s.token) window.localStorage.setItem(SITZ_KEY(eventId), JSON.stringify(s)); else window.localStorage.removeItem(SITZ_KEY(eventId)); } catch (_) { /* noop */ } }
function evHeaders(eventId, extra) {
  const h = buildHeaders(extra);
  const s = eventId ? holeSitzung(eventId) : null;
  if (s) h["X-Matchday-Sitzung"] = s.token;
  return h;
}
let zeitVersatz = 0, versatzGemessen = false;
function merkeServerZeit(serverZeit, gesendet) {
  if (!(serverZeit > 0)) return;
  const jetzt = Date.now(), mitte = gesendet ? (gesendet + jetzt) / 2 : jetzt;
  const neu = serverZeit - mitte;
  zeitVersatz = versatzGemessen ? zeitVersatz * 0.7 + neu * 0.3 : neu; versatzGemessen = true;
}
export function serverJetzt() { return Date.now() + Math.round(zeitVersatz); }
function bueroHeaders(bueroCode) {
  return buildHeaders(bueroCode ? { "X-Buero-Code": bueroCode } : null);
}
const ev = (eventId) => `${API_BASE}/events/${encodeURIComponent(eventId)}`;

// Fehler mit HTTP-Status, damit der Aufrufer z. B. 423 (Archiv,
// schreibgeschützt) von einem echten Netzproblem unterscheiden kann.
async function fail(res, what) {
  let msg = "";
  try { msg = (await res.json()).error || ""; } catch (_) { /* kein JSON */ }
  const err = new Error(`${what} fehlgeschlagen (HTTP ${res.status})${msg ? ": " + msg : ""}`);
  err.status = res.status;
  err.serverMessage = msg;
  throw err;
}

/* ---------- Stand eines Events ---------- */

// Lädt den zuletzt gespeicherten Stand DIESES Events. state === null heißt:
// Event existiert, aber es wurde noch nie gespeichert. Liefert zusätzlich
// den Event-Kopf (Status offen/abgeschlossen) mit.
export async function loadState(eventId, { bueroCode } = {}) {
  const t0 = Date.now();
  const res = await fetch(`${ev(eventId)}/state`, { headers: evHeaders(eventId, bueroCode ? { "X-Buero-Code": bueroCode } : null) });
  if (!res.ok) await fail(res, "Laden");
  const body = await res.json();
  merkeServerZeit(body.serverZeit, t0);
  return { id: body.id, state: body.state || null, event: body.event || null, rolle: body.rolle || null, teamId: body.teamId || null };
}

// Speichert den vollständigen Stand (ein Aufruf = eine neue Version).
// Bei einem abgeschlossenen Event antwortet der Server mit 423 — der Fehler
// trägt dann err.status === 423.
// Stand 36c: baseId = Version, auf der dieser Stand aufbaut. Hat inzwischen
// ein anderes Geraet gespeichert, antwortet der Server mit 409 (err.status).
export async function saveState(eventId, state, baseId) {
  const res = await fetch(`${ev(eventId)}/state`, {
    method: "POST",
    headers: evHeaders(eventId, { "X-Client-Zeit": String(serverJetzt()) }),
    body: JSON.stringify(baseId != null ? { state, baseId } : { state }),
  });
  if (!res.ok) await fail(res, "Speichern");
  return res.json();
}

// Sehr leichte Anfrage fürs Polling: nur { id, createdAt, status }.
export async function pollLatestId(eventId) {
  const t0 = Date.now();
  const res = await fetch(`${ev(eventId)}/state/meta`, { headers: evHeaders(eventId) });
  if (!res.ok) await fail(res, "Abfrage");
  const body = await res.json();
  merkeServerZeit(body.serverZeit, t0);
  return body && body.id != null ? body : null;
}

/* ---------- Events (Stand 34) ---------- */

export async function listEvents({ bueroCode, includeDeleted } = {}) {
  const q = includeDeleted ? "?all=1" : "";
  const res = await fetch(`${API_BASE}/events${q}`, { headers: bueroHeaders(bueroCode) });
  if (!res.ok) await fail(res, "Event-Liste");
  return (await res.json()).events || [];
}

// Neues Event { title, format, ort, datum, state } — state = Anfangsstand
// (vom Frontend aus blankState() gebaut), wird als erste Version gespeichert.
export async function createEvent(data, bueroCode) {
  // Stand 37o: Events legt nur das Büro an (Server prüft den Büro-Code)
  const res = await fetch(`${API_BASE}/events`, { method: "POST", headers: bueroHeaders(bueroCode), body: JSON.stringify(data) });
  if (!res.ok) await fail(res, "Event anlegen");
  return res.json(); // { ok, event, stateId }
}

// Wieder öffnen — mit Headcoach-PIN dieses Events ODER Büro-Code.
export async function reopenEvent(eventId, { pin, bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/reopen`, { method: "POST", headers: bueroHeaders(bueroCode), body: JSON.stringify({ pin: pin || "" }) });
  if (!res.ok) await fail(res, "Wieder öffnen");
  return res.json(); // { ok, event, id, state }
}

async function bueroPost(path, bueroCode, body) {
  const res = await fetch(path, { method: "POST", headers: bueroHeaders(bueroCode), body: JSON.stringify(body || {}) });
  if (!res.ok) await fail(res, "Büro-Aktion");
  return res.json();
}
export const deleteEvent = (eventId, bueroCode) => bueroPost(`${ev(eventId)}/delete`, bueroCode);
export const undeleteEvent = (eventId, bueroCode) => bueroPost(`${ev(eventId)}/undelete`, bueroCode);
export const setEventFreigabe = (eventId, value, bueroCode) => bueroPost(`${ev(eventId)}/freigabe`, bueroCode, { value: !!value });
export const checkBuero = (bueroCode) => bueroPost(`${API_BASE}/buero/check`, bueroCode);

/* ---------- Gäste-Ansicht (Stand 35): QR/Link, nur lesen ---------- */

// Bereinigter Stand eines Events über den geheimen Gäste-Schlüssel (ohne
// Zugangs-Codes). { id, state, event:{ title, format, ort, datum, status } }
export async function loadGast(token) {
  const res = await fetch(`${API_BASE}/gast/${encodeURIComponent(token)}`, { headers: buildHeaders() });
  if (!res.ok) await fail(res, "Laden");
  return res.json();
}
export async function pollGast(token) {
  const res = await fetch(`${API_BASE}/gast/${encodeURIComponent(token)}/meta`, { headers: buildHeaders() });
  if (!res.ok) await fail(res, "Abfrage");
  return res.json(); // { id }
}

/* ---------- Stand 37f: Touren über mehrere Events ---------- */
export async function listTours() {
  const res = await fetch(`${API_BASE}/tours`, { headers: buildHeaders() });
  if (!res.ok) await fail(res, "Touren laden");
  return (await res.json()).tours || [];
}
export async function loadTour(key) {
  const res = await fetch(`${API_BASE}/tours/${encodeURIComponent(key)}`, { headers: buildHeaders() });
  if (!res.ok) await fail(res, "Tour laden");
  return (await res.json()).tour || null;
}

/* ---------- Stand 37i: Afterwork-Teamticket-Abos ---------- */
export async function listAbos() {
  const res = await fetch(`${API_BASE}/abos`, { headers: buildHeaders() });
  if (!res.ok) await fail(res, "Abos laden");
  return (await res.json()).abos || [];
}
export async function createAbo(data, bueroCode) {
  const res = await fetch(`${API_BASE}/abos`, { method: "POST", headers: bueroHeaders(bueroCode), body: JSON.stringify(data) });
  if (!res.ok) await fail(res, "Abo anlegen");
  return res.json();
}
export async function setAboAktiv(id, aktiv, bueroCode) {
  const res = await fetch(`${API_BASE}/abos/${encodeURIComponent(id)}/aktiv`, { method: "POST", headers: bueroHeaders(bueroCode), body: JSON.stringify({ aktiv }) });
  if (!res.ok) await fail(res, "Abo ändern");
  return res.json();
}

/* ---------- Stand 37m: Büro — Träger/Projekt nachträglich, Kalender-Abo ---------- */
export const setEventZweck = (eventId, { traeger, projekt }, bueroCode) => bueroPost(`${ev(eventId)}/zweck`, bueroCode, { traeger, projekt });
export async function kalenderLink(bueroCode) {
  const res = await fetch(`${API_BASE}/kalender/link`, { headers: bueroHeaders(bueroCode) });
  if (!res.ok) await fail(res, "Kalender-Link");
  const pfad = (await res.json()).pfad; // "/kalender/<schlüssel>.ics" (liegt NICHT unter /api)
  const basis = typeof window !== "undefined" ? new URL(API_BASE, window.location.href) : null;
  return basis ? new URL(pfad, basis.origin).href : pfad;
}

/* ---------- Stand 37q: Orte (Microsoccer-Standorte + Veranstaltungsorte), nur Büro ---------- */
export async function listOrte(bueroCode) {
  const res = await fetch(`${API_BASE}/orte`, { headers: bueroHeaders(bueroCode) });
  if (!res.ok) await fail(res, "Orte laden");
  return (await res.json()).orte || [];
}
export const createOrt = (data, bueroCode) => bueroPost(`${API_BASE}/orte`, bueroCode, data);
export const updateOrt = (id, data, bueroCode) => bueroPost(`${API_BASE}/orte/${encodeURIComponent(id)}`, bueroCode, data);

/* ---------- Stand 37s: Stunden (nur Büro) ---------- */
export async function listStunden({ von, bis }, bueroCode) {
  const res = await fetch(`${API_BASE}/stunden?von=${encodeURIComponent(von)}&bis=${encodeURIComponent(bis)}`, { headers: bueroHeaders(bueroCode) });
  if (!res.ok) await fail(res, "Stunden laden");
  return res.json(); // { zeilen, personen }
}
export const bestaetigeStunde = (data, bueroCode) => bueroPost(`${API_BASE}/stunden/bestaetigen`, bueroCode, data);
export const speicherePerson = (data, bueroCode) => bueroPost(`${API_BASE}/personen`, bueroCode, data);

/* ---------- Stand 37v: Einstieg mit Code ---------- */
export async function einstieg(code, eventId) {
  const res = await fetch(`${API_BASE}/einstieg`, { method: "POST", headers: buildHeaders(), body: JSON.stringify({ code, ...(eventId ? { eventId } : {}) }) });
  if (!res.ok) await fail(res, "Einstieg");
  return (await res.json()).treffer || [];
}

/* ---------- Stand 38: Inhalte-Pipeline (Schnittstellen-Vertrag v1) ----------
   Bilder und Beiträge gehen an DIESEN Server (Ausgangskorb), der sie an den Playmaker-Eingang weiterreicht.
   Zugang: Code der eigenen Rolle in diesem Event (Headcoach/Coach) oder Büro-Code. */
function codeHeaders(code, bueroCode, extra, eventId) {
  const h = eventId ? evHeaders(eventId, extra) : buildHeaders(extra);
  if (code) h["X-Event-Code"] = code;
  if (bueroCode) h["X-Buero-Code"] = bueroCode;
  return h;
}
export async function ladeKatalog(bueroCode) {
  const res = await fetch(`${API_BASE}/katalog`, { headers: bueroHeaders(bueroCode) });
  if (!res.ok) await fail(res, "Katalog laden");
  const k = await res.json();
  return k && Array.isArray(k.formate) ? k : null;
}
export async function listeBeitraege(eventId, { code, bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/medien`, { headers: codeHeaders(code, bueroCode, null, eventId) });
  if (!res.ok) await fail(res, "Bilder laden");
  return res.json(); // { beitraege, einwilligung, bericht }
}
// Bild als Datei (roh, kein Base64). meta = { bildunterschrift, einwilligung, tags, academyId }
export async function sendeBild(eventId, datei, meta, { code, bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/medien`, { method: "POST",
    headers: codeHeaders(code, bueroCode, { "Content-Type": datei.type || "application/octet-stream", "X-Beitrag-Meta": encodeURIComponent(JSON.stringify(meta || {})) }, eventId),
    body: datei });
  if (!res.ok) await fail(res, "Hochladen");
  return res.json(); // { beitrag }
}
// Text oder Video-Link (typ "text" | "video")
export async function sendeBeitrag(eventId, daten, { code, bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/medien`, { method: "POST", headers: codeHeaders(code, bueroCode, null, eventId), body: JSON.stringify(daten || {}) });
  if (!res.ok) await fail(res, "Senden");
  return res.json();
}
// Bild (bereinigte Kopie ohne EXIF/GPS) als Blob-URL fürs <img>
export async function ladeBeitragBild(eventId, beitragId, { code, bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/medien/${encodeURIComponent(beitragId)}/datei`, { headers: codeHeaders(code, bueroCode, null, eventId) });
  if (!res.ok) await fail(res, "Bild laden");
  return URL.createObjectURL(await res.blob());
}
export async function setzeEventEinwilligung(eventId, wert, { code, bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/einwilligung`, { method: "POST", headers: codeHeaders(code, bueroCode, null, eventId), body: JSON.stringify({ wert }) });
  if (!res.ok) await fail(res, "Einwilligung speichern");
  return res.json();
}
export async function ladeBericht(eventId, { code, bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/bericht`, { headers: codeHeaders(code, bueroCode, null, eventId) });
  if (!res.ok) await fail(res, "Bericht laden");
  return res.json();
}
export async function sendeBerichtNeu(eventId, { code, bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/bericht/senden`, { method: "POST", headers: codeHeaders(code, bueroCode, null, eventId), body: "{}" });
  if (!res.ok) await fail(res, "Bericht senden");
  return res.json();
}
export async function outboxErneut(eventId, outboxId, { code, bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/ausgang/${encodeURIComponent(outboxId)}/erneut`, { method: "POST", headers: codeHeaders(code, bueroCode, null, eventId), body: "{}" });
  if (!res.ok) await fail(res, "Erneut senden");
  return res.json();
}

/* ---------- Stand 39: Anmelden, Academy-Login, Einsatzteam, Tore ---------- */
// Code (Headcoach-, Gast- oder Coach-Code[+Name]) → Sitzung für dieses Event (wird gleich gespeichert)
export async function anmelden(eventId, daten) {
  const res = await fetch(`${ev(eventId)}/sitzung`, { method: "POST", headers: buildHeaders(), body: JSON.stringify(daten || {}) });
  if (!res.ok) await fail(res, "Anmelden");
  const s = await res.json();
  setzeSitzung(eventId, { token: s.token, rolle: s.rolle, personId: s.personId || null, name: s.name || "" });
  return s;
}
export function abmelden(eventId) { setzeSitzung(eventId, null); }
// Rückkehr von der Academy: Einmal-Code → Person + passende Events (je mit eigener Sitzung)
export async function ssoEinloesen(code) {
  const res = await fetch(`${API_BASE}/sso/einloesen`, { method: "POST", headers: buildHeaders(), body: JSON.stringify({ code }) });
  if (!res.ok) await fail(res, "Academy-Login");
  return res.json(); // { person:{id,name}, treffer:[{eventId,title,datum,ort,rolle,team,token}] }
}
export function academyLoginUrl(weiter) {
  const basis = (typeof window !== "undefined" && window.MICROSOCCER_ACADEMY_URL) || "https://academy.microsoccer.social";
  return `${basis.replace(/\/+$/, "")}/api/sso.php?ziel=matchday${weiter ? "&weiter=" + encodeURIComponent(weiter) : ""}`;
}
export async function zaehleTor(eventId, { runde, lauf, delta, fuer }) {
  const t0 = Date.now();
  const res = await fetch(`${ev(eventId)}/tor`, { method: "POST", headers: evHeaders(eventId), body: JSON.stringify({ runde, lauf, delta, ...(fuer ? { fuer } : {}) }) });
  if (!res.ok) await fail(res, "Tor");
  const b = await res.json(); merkeServerZeit(b.serverZeit, t0); return b;
}
export async function speichereEinsatzteam(eventId, daten, { bueroCode } = {}) {
  const res = await fetch(`${ev(eventId)}/einsatzteam`, { method: "POST", headers: evHeaders(eventId, bueroCode ? { "X-Buero-Code": bueroCode } : null), body: JSON.stringify(daten || {}) });
  if (!res.ok) await fail(res, "Einsatzteam speichern");
  return res.json(); // { einsatzteam, gastCodes }
}
export async function academyPersonen({ eventId, bueroCode } = {}) {
  const res = await fetch(`${API_BASE}/academy/personen${eventId ? "?event=" + encodeURIComponent(eventId) : ""}`, { headers: evHeaders(eventId, bueroCode ? { "X-Buero-Code": bueroCode } : null) });
  if (!res.ok) await fail(res, "Academy-Personen");
  return res.json(); // { personen:[{id,name,einsatzrolle}] }
}
export async function bueroSitzung(eventId, bueroCode) {
  const res = await fetch(`${ev(eventId)}/sitzung`, { method: "POST", headers: buildHeaders({ "X-Buero-Code": bueroCode }), body: "{}" });
  if (!res.ok) await fail(res, "Büro-Sitzung");
  const s = await res.json();
  setzeSitzung(eventId, { token: s.token, rolle: s.rolle, personId: null, name: "Büro" });
  return s;
}

/* ---------- Stand 40: Usability-Schleife (Büro-Bericht) ---------- */
export async function uxBericht({ tage = 14, eventId = "" } = {}, bueroCode) {
  const q = new URLSearchParams({ tage: String(tage) }); if (eventId) q.set("eventId", eventId);
  const res = await fetch(`${API_BASE}/ux/bericht?${q}`, { headers: bueroHeaders(bueroCode) });
  if (!res.ok) await fail(res, "Usability-Bericht");
  return res.json();
}
export const uxStatusSetzen = (fp, status, notiz, bueroCode) => bueroPost(`${API_BASE}/ux/status`, bueroCode, { fp, status, notiz: notiz || "" });

/* ---------- Stand 40: Feierabend — Check-out / „Ich arbeite noch" / Freigabe (geht auch nach dem Abschluss) ---------- */
export async function einsatzAktion(eventId, body) {
  const res = await fetch(`${ev(eventId)}/einsatz`, { method: "POST", headers: evHeaders(eventId), body: JSON.stringify(body || {}) });
  if (!res.ok) await fail(res, "Einsatz");
  return res.json();
}
